import React from 'react'
import {Tooltip} from '~/src/design-system/BaseComponents'
import type {Notification} from '~/src/models/notification'
import {
  formatTimeAgo,
  getNotificationActionLabel,
  getNotificationBranch,
  NOTIFICATION_DOT_COLORS,
  isESignNotification,
} from '~/src/models/notification'
import {
  NotificationCardContainer,
  NotificationDot,
  NotificationContent,
  NotificationText,
  NotificationMeta,
  MetaText,
  MetaDivider,
  ActionButton,
} from '../../tasks/styles/TasksPageStyles'

interface NotificationCardProps {
  notification: Notification
  /**
   * Opens the notification's destination. Absent: it has none (a withdrawn job order invitation,
   * or a page this role cannot open), so the card shows no action button.
   */
  onOpenTask?: (notification: Notification) => void
}

const NotificationCard: React.FC<NotificationCardProps> = ({notification, onOpenTask}) => {
  const dotColor = NOTIFICATION_DOT_COLORS[notification.type] || 'var(--neutral-600)'
  const timeText = formatTimeAgo(notification.createdDate)
  const branchText = getNotificationBranch(notification)
  const fileName = notification.metadata?.fileName ?? ''
  const isESign = isESignNotification(notification.type)

  return (
    <NotificationCardContainer data-testid="notification-card">
      <NotificationDot $color={dotColor} />
      <NotificationContent>
        <NotificationText>{notification.message}</NotificationText>
        {onOpenTask && (
          <ActionButton onClick={() => onOpenTask(notification)} data-testid="open-task-button">
            {getNotificationActionLabel(notification.type, notification.ctaLink)}
          </ActionButton>
        )}
        <NotificationMeta>
          <Tooltip content={timeText} side="bottom">
            <MetaText>{timeText}</MetaText>
          </Tooltip>
          {branchText && (
            <>
              <MetaDivider />
              <Tooltip content={branchText} side="bottom">
                <MetaText>{branchText}</MetaText>
              </Tooltip>
            </>
          )}
          {isESign && fileName && (
            <>
              <MetaDivider />
              {fileName.length >= 18 ? (
                <Tooltip content={fileName}>
                  <MetaText>{fileName}</MetaText>
                </Tooltip>
              ) : (
                <MetaText>{fileName}</MetaText>
              )}
            </>
          )}
        </NotificationMeta>
      </NotificationContent>
    </NotificationCardContainer>
  )
}

export default NotificationCard
